/**
 * Renders a cacheable icon from public/icons/ as an <img> (or as a mask when useMaskForColor is true).
 * Icons are loaded from .svg files so they can be cached by the browser.
 */

import type { ImgHTMLAttributes } from 'react';
import { getIconSrc, type IconName, type IconVariant } from './iconPaths';

export type { IconName, IconVariant };

export interface IconProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  name: IconName;
  variant?: IconVariant;
  /** Optional alt text; defaults to '' (decorative). Use for accessibility when the icon conveys meaning. */
  alt?: string;
  /** When true, render as a mask so the icon inherits currentColor from parent/className. Still uses the same cacheable SVG URL. */
  useMaskForColor?: boolean;
}

/**
 * Icon component that loads SVGs from public/icons/ for better caching.
 * Use className for size (e.g. w-5 h-5). For color with img, use a wrapper; or set useMaskForColor so the icon uses currentColor.
 */
export function Icon({
  name,
  variant = 'outline',
  alt = '',
  className,
  style,
  useMaskForColor = false,
  ...imgProps
}: IconProps) {
  const src = getIconSrc(name, variant);
  if (!src) return null;

  if (useMaskForColor) {
    return (
      <span
        className={`icon-mask ${className ?? ''}`.trim()}
        style={{ ['--icon-url' as string]: `url(${src})`, ...style }}
        role={alt ? 'img' : undefined}
        aria-hidden={alt ? undefined : true}
        aria-label={alt || undefined}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      role={alt ? undefined : 'img'}
      aria-hidden={alt ? undefined : true}
      className={className}
      style={style}
      {...imgProps}
    />
  );
}
